import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { SearchHistoryItem } from '../../../../shared/interfaces/search-filter';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { AuthService } from '../../../auth/services/auth.service';
import { AppInsightsService } from '../../../../shared/services/app-insights/app-insights.service';
import { Menu, MenuList, NonMenuList } from '../../../../shared/models/menu';
import { CommonModule } from '@angular/common';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-search-box',
  standalone: true,
  imports: [CommonModule, FormsModule, ClickOutsideDirective, SvgIconComponent],
  templateUrl: './search-box.component.html',
  styleUrl: './search-box.component.scss',
})
export class SearchBoxComponent extends DestroyBase implements OnInit {
  menuData: Menu[] = [...MenuList, ...NonMenuList];
  suggestionsList: Menu[] = [];
  relatedSuggestions: Menu[] = [];

  router = inject(Router);
  authService = inject(AuthService);
  route = inject(ActivatedRoute);
  appInsightsService = inject(AppInsightsService);
  auditLogService = inject(AuditLogService);

  searchValue = '';
  showSuggestions = false;
  private searchInput$ = new Subject<void>();

  searchHistory: SearchHistoryItem[] = [];
  isSearchPanelOpen = false;

  // Keyword suggestions mapping
  private keywordMapping: Record<string, string[]> = {
    documents: ['files'],
    document: ['files'],
    doc: ['files'],
    docs: ['files'],
    file: ['files'],
    upload: ['files', 'profile'],
    uploads: ['files', 'profile'],
    uploading: ['files', 'profile'],
    records: ['custom', 'data axle', 'wncsbi', 'matched records'],
    record: ['custom', 'data axle', 'wncsbi', 'matched records'],
    business: ['custom', 'data axle', 'wncsbi'],
    map: ['dashboard'],
    maps: ['dashboard'],
    chart: ['dashboard'],
    charts: ['dashboard'],
    graph: ['dashboard'],
    toolkit: ['dashboard'],
    gbra: ['dashboard'],
    analytics: ['dashboard'],
    status: ['leads', 'dashboard'],
    setting: ['notifications', 'profile'],
    settings: ['notifications', 'profile'],
  };

  ngOnInit(): void {
    const storedSearchHistory = localStorage.getItem('search_history');

    if (storedSearchHistory) {
      this.searchHistory = JSON.parse(storedSearchHistory);
    }
    this.searchInput$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterSuggestions();
      });
  }

  onSearch(): void {
    this.searchValue = this.searchValue.trim();
    if (!this.searchValue) {
      this.showSuggestions = false;
      this.isSearchPanelOpen = this.searchHistory.length > 0;
      return;
    }
    this.isSearchPanelOpen = true;
    this.searchInput$.next();
  }

  filterSuggestions(): void {
    this.suggestionsList = [];
    this.relatedSuggestions = [];

    if (!this.searchValue) {
      this.showSuggestions = false;
      return;
    }

    const searchLower = this.searchValue.toLowerCase();

    this.menuData.forEach((item) => {
      if (!this.authService.hasPermission(item.permission)) return;

      if (item.name.toLowerCase().startsWith(searchLower)) {
        this.suggestionsList.push(item);
        return;
      }

      if (item.sub.length > 0) {
        const filteredSubRoutes = item.sub.filter(
          (sub) =>
            sub.name.toLowerCase().startsWith(searchLower) &&
            this.authService.hasPermission(sub.permission),
        );

        if (filteredSubRoutes.length > 0) {
          this.suggestionsList.push({ ...item, sub: filteredSubRoutes });
        }
      }
    });

    // If no direct matches found, check for related keywords
    if (this.suggestionsList.length === 0) {
      this.findRelatedSuggestions(searchLower);
    }

    this.showSuggestions = true;
  }

  private findRelatedSuggestions(searchTerm: string): void {
    // Check if search term matches any keyword
    const relatedKeywords = this.keywordMapping[searchTerm];

    if (relatedKeywords && relatedKeywords.length > 0) {
      // Find menu items that match the related keywords
      relatedKeywords.forEach((keyword) => {
        this.menuData.forEach((item) => {
          if (!this.authService.hasPermission(item.permission)) return;

          // Check main menu item
          if (item.name.toLowerCase().includes(keyword.toLowerCase())) {
            if (!this.relatedSuggestions.some((s) => s.route === item.route)) {
              this.relatedSuggestions.push({ ...item, sub: [] });
            }
          }

          // Check sub-menu items
          if (item.sub.length > 0) {
            const matchingSubRoutes = item.sub.filter(
              (sub) =>
                sub.name.toLowerCase().includes(keyword.toLowerCase()) &&
                this.authService.hasPermission(sub.permission),
            );

            if (matchingSubRoutes.length > 0) {
              matchingSubRoutes.forEach((sub) => {
                const exists = this.relatedSuggestions.some(
                  (s) =>
                    s.route === sub.route &&
                    this.relatedSuggestions.find(
                      (rs) => rs.route === sub.route,
                    ),
                );
                if (!exists) {
                  this.relatedSuggestions.push({ ...item, sub: [sub] });
                }
              });
            }
          }
        });
      });
    }
  }

  onSuggestionClick(item: Menu, parentRoute = '', parentRouteName = ''): void {
    this.searchValue = '';
    this.showSuggestions = false;
    this.suggestionsList = [];
    this.isSearchPanelOpen = false;
    let route = parentRoute ? `${parentRoute}/${item.route}` : item.route;
    let queryParams = item.queryParams ?? {};

    if (item.sub?.length > 0) {
      route = `${route}/${item.sub[0].route}`;
      queryParams = item.sub[0].queryParams ?? {};
    }

    const exists = this.searchHistory.some(
      (h) => h.menuItem.route === item.route && h.parentRoute === parentRoute,
    );

    if (!exists) {
      this.searchHistory.unshift({
        menuItem: item,
        parentRoute: parentRoute,
        parentRouteName: parentRouteName,
      });

      this.searchHistory = this.searchHistory.slice(0, 5);

      localStorage.setItem(
        'search_history',
        JSON.stringify(this.searchHistory),
      );
    }

    // Log search event to Azure Application Insights
    this.appInsightsService.logEvent('GlobalSearchPerformed', {
      connectionName: this.authService.getLoggedInUserConnectionName() || '',
      searchValue: this.searchValue,
      searchHistoryItem: { menuItem: item, parentRoute: parentRoute },
      timestamp: new Date().toISOString(),
    });

    this.router.navigate([route], { queryParams });
  }

  toggleSearchPanel(): void {
    this.isSearchPanelOpen = false;
    this.showSuggestions = false;
  }

  removeFromHistory(history: SearchHistoryItem): void {
    this.searchHistory = this.searchHistory.filter(
      (h) =>
        !(
          h.menuItem.route === history.menuItem.route &&
          h.parentRoute === history.parentRoute
        ),
    );
    this.suggestionsList = [];
    localStorage.setItem('search_history', JSON.stringify(this.searchHistory));
    if (this.searchHistory.length == 0) {
      this.isSearchPanelOpen = false;
    }
  }

  onSearchClick() {
    this.isSearchPanelOpen = this.searchHistory.length > 0;
  }

  clearSearch(): void {
    this.searchValue = '';
    this.showSuggestions = false;
    this.suggestionsList = [];
    this.isSearchPanelOpen = this.searchHistory.length > 0;
  }

  onSearchRecordsClick(): void {
    this.isSearchPanelOpen = false;
    this.auditLogService.logFeature({
      featureName: 'Global record search',
      action: 'Record search from search box',
      meta: { searchQuery: this.searchValue },
      description: `Search for ${this.searchValue} is triggered`,
    });
    this.router.navigate(['/search'], {
      queryParams: {
        primary_name: this.searchValue,
        sources: 'PUBLIC,CUSTOM_BUSINESS_RECORDS,WNCSBI_APPS',
      },
    });
  }
}
